import type {
  Asistente,
  Prefix,
  Category,
  RegisterData,
  PrefixUpdateData,
  ApproveCategoryData,
  ChangeCategoryData,
  Group,
} from "../types"
import type { ApiResponse, DynamicUser, DynamicSchema, DynamicCreateRequest, DynamicCreateResponse, DynamicUpdatePayload } from "../types/dynamic"

const API_BASE_URL = "https://api.congresoinmedgral.com"

// Global API operation tracking
let apiOperationCallback: ((isActive: boolean) => void) | null = null

export const setApiOperationCallback = (callback: (isActive: boolean) => void) => {
  apiOperationCallback = callback
}

// Helper to wrap API calls with operation tracking
const withApiTracking = async <T>(operationName: string, apiCall: () => Promise<T>): Promise<T> => {
  try {
    console.log(`🌐 Iniciando operación API: ${operationName}`)
    apiOperationCallback?.(true)
    const result = await apiCall()
    console.log(`✅ Operación API completada: ${operationName}`)
    return result
  } catch (error) {
    console.error(`❌ Error en operación API ${operationName}:`, error)
    throw error
  } finally {
    apiOperationCallback?.(false)
  }
}

export const fetchAsistentes = async (): Promise<Asistente[]> => {
  const MAX_RETRIES = 3
  const INITIAL_TIMEOUT = 30000 // 30 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      const response = await fetch(`${API_BASE_URL}/get_asistentes.php`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1) // Backoff exponencial
        console.log(`⏳ Reintentando (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch asistentes after retries')
}

export const updateAsistente = async (asistente: Asistente): Promise<void> => {
  return withApiTracking("updateAsistente", async () => {
    const response = await fetch(`${API_BASE_URL}/update_asistente.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(asistente),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.message || "Error updating asistente")
    }
  })
}

export const updatePrefix = async (data: PrefixUpdateData): Promise<void> => {
  return withApiTracking("updatePrefix", async () => {
    const response = await fetch(`${API_BASE_URL}/update_prefix.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.message || "Error updating prefix")
    }
  })
}

export const approveCategory = async (data: ApproveCategoryData): Promise<void> => {
  return withApiTracking("approveCategory", async () => {
    const response = await fetch(`${API_BASE_URL}/approve_category.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        asistente_id: Number.parseInt(data.asistente_id),
      }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.message || "Error approving category")
    }
  })
}

export const fetchPrefixes = async (): Promise<Prefix[]> => {
  const MAX_RETRIES = 2
  const INITIAL_TIMEOUT = 20000 // 20 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      const response = await fetch(`${API_BASE_URL}/get_prefixes.php`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando prefijos (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1)
        console.log(`⏳ Reintentando prefijos (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch prefixes after retries')
}

export const fetchCouponPrefixes = async (): Promise<string[]> => {
  const MAX_RETRIES = 2
  const INITIAL_TIMEOUT = 20000 // 20 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      const response = await fetch(`${API_BASE_URL}/get_coupon_prefixes.php`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando prefijos de cupón (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1)
        console.log(`⏳ Reintentando prefijos de cupón (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch coupon prefixes after retries')
}

export const fetchCategories = async (): Promise<Category[]> => {
  const MAX_RETRIES = 2
  const INITIAL_TIMEOUT = 20000 // 20 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      const response = await fetch(`${API_BASE_URL}/get_categories.php`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando categorías (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1)
        console.log(`⏳ Reintentando categorías (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch categories after retries')
}

export const registerSimple = async (
  data: RegisterData,
): Promise<{
  success: boolean
  asistente_id?: string
  folio?: string
  category?: Category
  error?: string
  debug?: string
}> => {
  return withApiTracking("registerSimple", async () => {
    const response = await fetch(`${API_BASE_URL}/register_simple.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result
  })
}

export const changeCategory = async (data: ChangeCategoryData): Promise<void> => {
  return withApiTracking("changeCategory", async () => {
    const response = await fetch(`${API_BASE_URL}/change_category.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        asistente_id: Number.parseInt(data.asistente_id),
        category_id: data.category_id,
      }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.message || "Error changing category")
    }
  })
}

const PAYMENT_LINKS: Record<number, string> = {
  2: "https://buy.stripe.com/fZeaF85qUekEgJW9AI",
  3: "https://buy.stripe.com/14k3cG8D67Wg2T63cg",
  4: "https://buy.stripe.com/6oEdRkcTmb8s1P2eUZ",
  5: "https://buy.stripe.com/4gw3cG7z25O865i007",
  7: "https://buy.stripe.com/9AQaF84mQ0tOeBO4gm"
};

export const fetchGroups = async (): Promise<Group> => {
  const MAX_RETRIES = 3
  const INITIAL_TIMEOUT = 30000 // 30 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      const response = await fetch(`${API_BASE_URL}/grupos.php`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando grupos (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1) // Backoff exponencial
        console.log(`⏳ Reintentando grupos (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch groups after retries')
}

export const sendPaymentLinkEmail = async (asistente: Asistente): Promise<boolean> => {
  const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzCUCHffsmQCkiTXkXsSKDjVlRK16778hHu7FIKdDSYUKQ8rTCMyXRmIDjG6NSLzz2h6g/exec";
  
  // Convertir tipo_asistente_id a number de forma segura
  const tipoAsistenteId = asistente.tipo_asistente_id ? Number(asistente.tipo_asistente_id) : null;
  
  if (!tipoAsistenteId || !PAYMENT_LINKS[tipoAsistenteId]) {
    throw new Error("Categoría no válida o sin enlace de pago configurado");
  }

  const payload = {
    apiKey: "f4a7d8c3b2e1a9f5c8d7b6a4e3f1d2c9b8a7e6d5c4b3a2f1e0d9c8b7a6f5e4d3",
    sender: "America Digital",
    subject: "Enlace de pago para completar tu registro",
    template: "v2_enlace_pago_congresoinmedgral_2025",
    user: {
      email: asistente.asistente_correo,
      nombre: asistente.asistente_nombre,
      url: PAYMENT_LINKS[tipoAsistenteId],
      url_href: `href="${PAYMENT_LINKS[tipoAsistenteId]}"`
    }
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return true;
  } catch (error) {
    console.error('Error al enviar correo:', error);
    return false;
  }
}

// =============================================================================
// DYNAMIC SYSTEM API FUNCTIONS
// =============================================================================

const DYNAMIC_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

/**
 * Fetches dynamic users and schema from the new API endpoint
 */
export const fetchDynamicData = async (): Promise<ApiResponse> => {
  const MAX_RETRIES = 3
  const INITIAL_TIMEOUT = 30000 // 30 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      console.log(`🌐 Obteniendo datos dinámicos del nuevo API (intento ${attempt + 1})...`)
      
      const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando datos dinámicos (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: ApiResponse = await response.json()
      
      // Validar la estructura de la respuesta
      if (!data.data || !data.data.schema || !Array.isArray(data.data.users)) {
        throw new Error("Estructura de respuesta inválida del API dinámico")
      }
    
      console.log(`✅ Datos dinámicos obtenidos: ${data.data.users.length} usuarios, ${Object.keys(data.data.schema).length} campos de schema`)
      return data
      
    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1) // Backoff exponencial
        console.log(`⏳ Reintentando datos dinámicos (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch dynamic data after retries')
}

/**
 * Updates a dynamic user via API
 */
export const updateDynamicUser = async (user: DynamicUser): Promise<void> => {
  return withApiTracking("updateDynamicUser", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/users/${user.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(user),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (result.status !== 200) {
      throw new Error(result.message || "Error updating dynamic user")
    }
  })
}


/**
 * Creates one or multiple dynamic users using the specific API format
 */
export const createDynamicUser = async (createRequest: DynamicCreateRequest | DynamicCreateRequest[]): Promise<DynamicCreateResponse | DynamicUser[]> => {
  return withApiTracking("createDynamicUser", async () => {
    console.log("⚠️ Imprimiendo createRequest para depuración:", createRequest)
    const isArray = Array.isArray(createRequest)
    console.log(`🌐 Creando ${isArray ? 'múltiples usuarios' : 'usuario'} dinámico${isArray ? 's' : ''}:`, createRequest)
    
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/users/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(createRequest),
    })

    console.log("⚠️ Respuesta recibida del servidor:", response)

    if (!response.ok) {
      console.log("❌ Respuesta no OK del servidor:", response.status)
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()

    console.log("⚠️ Resultado parseado del servidor:", result)
    
    if (isArray) {
      // Para array, esperamos una respuesta con array de usuarios creados
      if (result.status !== 201 && result.status !== 200) {
        throw new Error(result.message || "Error creating dynamic users")
      }
      
      // La respuesta puede ser directamente un array o tener la estructura { data: users }
      const users = Array.isArray(result) ? result : (result.data || result.users || [])
      console.log(`✅ ${users.length} usuarios dinámicos creados exitosamente`)
      return users
    } else {
      // Para objeto único, mantenemos la respuesta original
      if (result.status !== 200) {
        throw new Error(result.message || "Error creating dynamic user")
      }
      
      console.log('✅ Usuario dinámico creado exitosamente:', result.data)
      console.log("⚠️ Respuesta completa del servidor:", result)
      return result
    }
  })
}

/**
 * Updates a dynamic user with only changed fields using unique field identifier
 */
export const updateDynamicUserByUniqueField = async (payload: DynamicUpdatePayload): Promise<void> => {
  return withApiTracking("updateDynamicUserByUniqueField", async () => {
    console.log('🌐 Actualizando usuario dinámico:', payload.uniqueFieldValue, payload.updateData)
    
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/users/${payload.uniqueFieldValue}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload.updateData),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (result.status !== 200) {
      throw new Error(result.message || "Error updating dynamic user")
    }

    console.log('✅ Usuario dinámico actualizado exitosamente')
  })
}

/**
 * Legacy function wrapper for backward compatibility
 * This allows existing code to continue working while we transition
 */
export const fetchDynamicUsers = async (): Promise<{ users: DynamicUser[], schema: DynamicSchema }> => {
  const response = await fetchDynamicData()
  return {
    users: response.data.users,
    schema: response.data.schema
  }
}
